/**
 * SuccessDialog — "done ✓" confirmation shown AFTER an action completes (ported
 * from sk-catalog). The positive counterpart to ConfirmDialog. Billing theme +
 * SkIcon.
 */
import React from 'react';
import { Modal, View, Text, Pressable, StyleSheet, Dimensions, Platform, TouchableOpacity } from 'react-native';
import SkIcon from './skIcon';
import { useTheme } from '../context/themeContext';

const { width } = Dimensions.get('window');

type DialogAction = Readonly<{ label: string; onPress: () => void }>;

type SuccessDialogProps = Readonly<{
  visible: boolean;
  title?: string;
  message?: string;
  summary?: string[];
  tone?: 'success' | 'warning';
  primaryAction?: DialogAction;
  secondaryAction?: DialogAction;
  onClose?: () => void;
}>;

export default function SuccessDialog({
  visible,
  title = 'All done',
  message,
  summary,
  tone = 'success',
  primaryAction,
  secondaryAction,
  onClose,
}: SuccessDialogProps) {
  const { theme } = useTheme();
  const { colors, font } = theme;

  const TONES = {
    success: { accent: colors.success, soft: colors.successSoft, icon: 'checkCircle' as const },
    warning: { accent: colors.warn, soft: colors.warnSoft, icon: 'alert' as const },
  };
  const t = TONES[tone];

  return (
    <Modal transparent visible={visible} animationType="fade" onRequestClose={onClose}>
      <Pressable style={styles.overlay} onPress={onClose}>
        <Pressable style={[styles.box, { backgroundColor: colors.card }]} onPress={() => {}}>
          <View style={[styles.indicator, { backgroundColor: t.accent }]} />

          {onClose && (
            <TouchableOpacity
              style={[styles.closeButton, { backgroundColor: colors.paper2 }]}
              onPress={onClose}
              hitSlop={{ top: 20, bottom: 20, left: 20, right: 20 }}
            >
              <SkIcon name="x" size={20} color={colors.ink4} />
            </TouchableOpacity>
          )}

          <View style={styles.content}>
            <View style={[styles.badge, { backgroundColor: t.soft }]}>
              <SkIcon name={t.icon} size={28} color={t.accent} strokeWidth={2} />
            </View>

            <Text style={[styles.title, { color: colors.ink, fontFamily: font.sansBold }]}>{title}</Text>
            {!!message && <Text style={[styles.message, { color: colors.ink3, fontFamily: font.sans }]}>{message}</Text>}

            {!!summary?.length && (
              <View style={[styles.summary, { backgroundColor: colors.paper2 }]}>
                {summary.map((line, i) => (
                  <View key={i} style={styles.summaryRow}>
                    <SkIcon name="check" size={14} color={t.accent} strokeWidth={2.4} />
                    <Text style={[styles.summaryText, { color: colors.ink2, fontFamily: font.sans }]}>{line}</Text>
                  </View>
                ))}
              </View>
            )}
          </View>

          {(primaryAction || secondaryAction) && (
            <View style={[styles.footer, { backgroundColor: colors.paper2 }]}>
              {secondaryAction && (
                <Pressable style={({ pressed }) => [styles.btn, { backgroundColor: colors.paper3 }, pressed && styles.btnPressed]} onPress={secondaryAction.onPress}>
                  <Text style={[styles.btnText, { color: colors.ink2, fontFamily: font.sansSemibold }]}>{secondaryAction.label}</Text>
                </Pressable>
              )}
              {primaryAction && (
                <Pressable style={({ pressed }) => [styles.btn, { backgroundColor: t.accent }, pressed && styles.btnPressed]} onPress={primaryAction.onPress}>
                  <Text style={[styles.btnText, { color: '#fff', fontFamily: font.sansSemibold }]}>{primaryAction.label}</Text>
                </Pressable>
              )}
            </View>
          )}
        </Pressable>
      </Pressable>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: { flex: 1, backgroundColor: 'rgba(15, 23, 42, 0.55)', justifyContent: 'center', alignItems: 'center', paddingHorizontal: 24 },
  box: {
    width: Math.min(width * 0.85, 360), borderRadius: 16, overflow: 'hidden',
    ...(Platform.OS === 'web' ? ({ boxShadow: '0 16px 48px rgba(16,24,40,.25)' } as any) : { elevation: 10 }),
  },
  indicator: { height: 4, width: '100%' },
  content: { paddingTop: 24, paddingHorizontal: 20, paddingBottom: 18, alignItems: 'center' },
  badge: { width: 56, height: 56, borderRadius: 28, justifyContent: 'center', alignItems: 'center', marginBottom: 14 },
  title: { fontSize: 17, fontWeight: '700', textAlign: 'center', marginBottom: 6, letterSpacing: -0.2 },
  message: { fontSize: 13, textAlign: 'center', lineHeight: 19 },
  summary: { alignSelf: 'stretch', marginTop: 16, gap: 8, borderRadius: 10, padding: 12 },
  summaryRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  summaryText: { fontSize: 13, flex: 1 },
  footer: { flexDirection: 'row', padding: 14, gap: 10 },
  btn: { flex: 1, height: 44, borderRadius: 10, justifyContent: 'center', alignItems: 'center' },
  btnPressed: { transform: [{ translateY: 0.5 }], opacity: 0.85 },
  btnText: { fontSize: 14, fontWeight: '600' },
  closeButton: { position: 'absolute', top: 14, right: 14, zIndex: 10, borderRadius: 16, width: 30, height: 30, justifyContent: 'center', alignItems: 'center' },
});
